import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import ClearOnboardingConfirmModal from '@/components/test-users/ClearOnboardingConfirmModal';
import { useClearTestUserOnboarding, useTestUsers } from '@/hooks/useTestUsers';

const COLUMNS = ['Email', 'Nickname', 'Children', 'Onboarding assessment', 'Programme', 'Actions'];

export default function ClearOnboardingPage() {
  const { data, isLoading, isError, error } = useTestUsers();
  const clearOnboarding = useClearTestUserOnboarding();
  const [target, setTarget] = useState<string | null>(null);

  // Modal stays open on error so the admin can retry or cancel.
  const handleConfirm = () => {
    if (!target) return;
    clearOnboarding.mutate(target, {
      onSuccess: (result) => {
        toast.success(`Cleared onboarding for ${result.email} (${result.totalDeleted} rows deleted)`);
        setTarget(null);
      },
      onError: (e) => toast.error(e.message),
    });
  };

  useEffect(() => {
    document.title = 'Clear Onboarding | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load test users');
    }
  }, [isError, error]);

  return (
    <div className="space-y-6" data-testid="clear-onboarding-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-pine-display font-display font-semibold text-pine-ink">
            Clear Onboarding
          </h1>
          <p className="text-pine-body text-pine-ink-soft mt-1">
            Reset onboarding for test accounts. Clearing deletes the account's children,
            assessments and programme registration; the login is kept.
          </p>
        </div>
      </motion.div>

      {isError && (
        <div className="admin-card p-6 border border-pine-danger/20 bg-pine-danger/5">
          <p className="text-pine-danger">Failed to load test users. Please try again.</p>
        </div>
      )}

      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-pine-line">
                  {COLUMNS.map((label) => (
                    <th
                      key={label}
                      className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 7 }, (_, i) => (
                      <tr key={i} className="border-b border-pine-line">
                        <td colSpan={COLUMNS.length} className="px-6 py-4">
                          <Skeleton className="h-5 w-full" />
                        </td>
                      </tr>
                    ))
                  : data?.users.map((user) => (
                      <tr
                        key={user.email}
                        data-testid={`test-user-row-${user.email}`}
                        className={`border-b border-pine-line ${user.exists ? '' : 'text-pine-ink-soft opacity-60'}`}
                      >
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span>{user.email}</span>
                            {!user.exists && <Badge variant="outline">No account</Badge>}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">{user.nickname ?? '—'}</td>
                        <td className="px-6 py-4 text-sm">{user.childCount}</td>
                        <td className="px-6 py-4 text-sm">
                          {user.onboardingAssessmentCount || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm">{user.programmeState ?? '—'}</td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            data-testid={`clear-onboarding-${user.email}`}
                            disabled={!user.exists}
                            onClick={() => setTarget(user.email)}
                            className="px-3 py-1.5 text-sm font-semibold rounded-pine-input border-[1.5px] border-pine-danger text-pine-danger hover:bg-pine-danger hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Clear onboarding
                          </button>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </Card>
        </motion.div>
      )}

      <ClearOnboardingConfirmModal
        open={target !== null}
        email={target ?? ''}
        onClose={() => setTarget(null)}
        onConfirm={handleConfirm}
        isLoading={clearOnboarding.isPending}
      />
    </div>
  );
}
